/**
 * Cross-browser fullscreen helpers.
 *
 * Desktop / Android Chrome: Element Fullscreen API (with webkit prefixes).
 * iPhone Safari: only the <video> can go fullscreen via webkitEnterFullscreen;
 * arbitrary-element requestFullscreen is unsupported or no-ops. That call must
 * stay synchronous inside the user-gesture stack or Safari rejects it.
 */

export type WebkitHTMLVideoElement = HTMLVideoElement & {
  webkitSupportsFullscreen?: boolean;
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type PrefixedFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitRequestFullScreen?: () => Promise<void> | void;
};

/** iPhone / iPod / iPad (including iPadOS desktop-UA). */
export const isAppleTouchDevice = (): boolean => {
  if (typeof navigator === "undefined") return false;
  if (/iPhone|iPod|iPad/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ reports as MacIntel but is a touch tablet.
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

/** True when this element can be promoted to fullscreen on its own. */
export const supportsElementFullscreen = (el: HTMLElement): boolean => {
  const target = el as PrefixedFullscreenElement;
  return (
    typeof target.requestFullscreen === "function" ||
    typeof target.webkitRequestFullscreen === "function" ||
    typeof target.webkitRequestFullScreen === "function"
  );
};

export const getFullscreenElement = (): Element | null => {
  const doc = document as WebkitDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

export const exitElementFullscreen = async (): Promise<void> => {
  const doc = document as WebkitDocument;
  if (document.fullscreenElement) {
    await document.exitFullscreen();
    return;
  }
  if (doc.webkitFullscreenElement && typeof doc.webkitExitFullscreen === "function") {
    await doc.webkitExitFullscreen();
  }
};

/** Returns true when the element entered (or was already in) document fullscreen. */
export const requestElementFullscreen = async (el: HTMLElement): Promise<boolean> => {
  const target = el as PrefixedFullscreenElement;
  try {
    if (typeof target.requestFullscreen === "function") {
      await target.requestFullscreen();
      return getFullscreenElement() != null;
    }
    if (typeof target.webkitRequestFullscreen === "function") {
      await target.webkitRequestFullscreen();
      return getFullscreenElement() != null;
    }
    if (typeof target.webkitRequestFullScreen === "function") {
      await target.webkitRequestFullScreen();
      return getFullscreenElement() != null;
    }
  } catch {
    return false;
  }
  return false;
};

type LockableScreenOrientation = ScreenOrientation & {
  lock?: (orientation: "landscape") => Promise<void>;
  unlock?: () => void;
};

const screenOrientation = (): LockableScreenOrientation | null => {
  if (typeof screen === "undefined") return null;
  return (screen.orientation as LockableScreenOrientation | undefined) ?? null;
};

/**
 * Turn a phone sideways for a landscape clip while it is fullscreen.
 *
 * A viewer with the system rotation lock on otherwise gets a 16:9 clip squeezed
 * into a portrait strip and no way to turn it, because rotating the handset does
 * nothing. `screen.orientation.lock` overrides that system lock for as long as
 * something is fullscreen, which is how every other video site gets landscape.
 *
 * Only Chromium implements it (Android is what matters here); Safari ships no
 * usable `lock`, so on iPhone this fails and the OS decides — see
 * `isPortraitViewport`, which is how the player detects that and turns the clip
 * sideways itself. The call also rejects unless an element is already
 * fullscreen, so it has to run *after* entering, never alongside.
 *
 * Resolves true when the browser took the lock, false when it has no `lock` or
 * refused — the caller needs to know which, because a refusal is what puts the
 * player's own sideways fallback in charge.
 */
export const lockLandscapeOrientation = async (): Promise<boolean> => {
  const orientation = screenOrientation();
  if (typeof orientation?.lock !== "function") return false;
  try {
    await orientation.lock("landscape");
    return true;
  } catch {
    // Unsupported, or the browser refused (not fullscreen / desktop). Leave the
    // orientation alone rather than treating it as an error worth surfacing.
    return false;
  }
};

/**
 * Is the viewport taller than it is wide right now?
 *
 * This is the *presented* orientation, not the physical one: when a handset is
 * held sideways but the page has not been allowed to turn (rotation lock, a
 * browser that refuses to rotate a fullscreen page), this still reports
 * portrait — which is exactly the case the player rotates the clip for.
 */
export const isPortraitViewport = (): boolean => {
  if (typeof window === "undefined") return false;
  const query = window.matchMedia?.("(orientation: portrait)");
  if (query) return query.matches;
  return window.innerHeight > window.innerWidth;
};

/** Subscribe to viewport orientation flips. Returns an unsubscribe function. */
export const subscribeToOrientation = (onChange: () => void): (() => void) => {
  const query = window.matchMedia?.("(orientation: portrait)");
  query?.addEventListener("change", onChange);
  // iOS fires `orientationchange`/`resize` on rotation even when the media
  // query result is unchanged (e.g. a rotation-locked page), and the dynamic
  // viewport units move with the toolbars, so listen for those too.
  window.addEventListener("orientationchange", onChange);
  window.addEventListener("resize", onChange);
  return () => {
    query?.removeEventListener("change", onChange);
    window.removeEventListener("orientationchange", onChange);
    window.removeEventListener("resize", onChange);
  };
};

/** Hand rotation back to the OS. Browsers also do this on fullscreen exit. */
export const unlockOrientation = (): void => {
  const orientation = screenOrientation();
  if (typeof orientation?.unlock !== "function") return;
  try {
    orientation.unlock();
  } catch {
    // Nothing was locked, or the browser has no say. Either way, nothing to do.
  }
};

export const isWebkitVideoFullscreen = (video: HTMLVideoElement | null): boolean => {
  if (!video) return false;
  return Boolean((video as WebkitHTMLVideoElement).webkitDisplayingFullscreen);
};

export const enterWebkitVideoFullscreen = (video: HTMLVideoElement): boolean => {
  const v = video as WebkitHTMLVideoElement;
  if (typeof v.webkitEnterFullscreen !== "function") return false;
  if (v.webkitSupportsFullscreen === false) return false;
  try {
    v.webkitEnterFullscreen();
    return true;
  } catch {
    return false;
  }
};

export const exitWebkitVideoFullscreen = (video: HTMLVideoElement): boolean => {
  const v = video as WebkitHTMLVideoElement;
  if (!v.webkitDisplayingFullscreen || typeof v.webkitExitFullscreen !== "function") {
    return false;
  }
  try {
    v.webkitExitFullscreen();
    return true;
  } catch {
    return false;
  }
};
